import { useEffect, useRef, useState } from 'react'
import { GanttTask, Partner, Dependency } from '../../types/gantt.types'
import type { HolidayState } from '../../hooks/useHolidays'
import { PeriodEditPopover } from '../PeriodEditPopover'
import { PartnerCardSignal } from '../PartnerCard'
import { SiteInfo } from '../../views/CalendarMultiView'
import { MaterialOrderChange } from '../../views/MaterialOrderPopup'
import { TaskDetailPanel } from '../TaskDetailPanel'
import { ConfirmProgressSummary } from '../ConfirmProgressSummary'
import { getConfirmProgressSteps, PARTNER_STATUS } from '../../constants/partnerStatus'
import {
  appendWorkProgressSteps, getWorkProgressSteps, WORK_ORDER_DETAIL_KEY, WORK_REPORT_DETAIL_KEY, WORK_PROGRESS_TEXT,
} from '../../constants/taskProgress.logic'
import { useTaskProgressSummary } from '../../hooks/useTaskProgress'
import { WorkOrderDetailModal } from '../progress/WorkOrderDetailModal'
import { WorkReportDetailModal } from '../progress/WorkReportDetailModal'
import { MaterialPartnerCard } from '../material/MaterialPartnerCard'
import { OrderModeCard } from '../orderMode/OrderModeCard'
import { useMaterialOrderData } from '../../hooks/useMaterialOrders'
import { CustReqCard } from '../custReq/CustReqCard'
import { isoDay, workStatusWritePath } from '../../constants/signalView'
import { getPredecessorCandidates, workdaysOrNull, type CascadePlan } from '../../views/scheduleUtils'
import { useSchedulerHost } from '../../context/SchedulerHostContext'
import { isDirectRecord } from '@indipia/shared'
// 공종 정보 사이드 패널(TaskDetailPanel) 어댑터

/** 결과만 직접등록(SIF-3) 공종 — 진행 요약 '작업보고' 단계의 [작업현황 등록 ›] 링크(이동할 화면은 workStatusWritePath) */
const WORK_STATUS_WRITE_ACTION = { key: 'work-status-write', label: '작업현황 등록' }
/** 결과만 직접등록 공종의 진행 요약 마지막 단계 이름('작업보고' 대신) */
const WORK_STATUS_STEP_TITLE = '작업현황'

export type GanttDetailPanelHostProps = {
  tasks: GanttTask[]
  selectedPanelTaskId: any
  panelTaskRef: React.MutableRefObject<GanttTask | null>
  panelWorkTypeName: any
  setPanelWorkTypeName: any
  partners: Partner[]
  panelPartnerId: any
  panelForeman: any
  setPanelPartnerId: any
  setPanelForeman: any
  handlePartnerSent: any
  panelChangeReason: any
  setPanelChangeReason: any
  handlePartnerChanged: any
  panelForemanSignal: (t: GanttTask | null) => PartnerCardSignal
  selectedSite: SiteInfo | undefined
  cardSup: { taskId: number; nonce: number; } | null
  /** 딥링크 card=pt — 이 공종이면 시공파트너 카드의 진행 요약을 펼친다 */
  cardPt: { taskId: number; nonce: number; } | null
  /** 딥링크 card=cr — 이 공종이면 고객 요청 카드를 화면에 보이게 한다(선택: 호출 화면이 아직 넘기지 않아도 동작) */
  cardCr?: { taskId: number; nonce: number; } | null
  /** 고객 요청 등록·수정·삭제·사진 변경 성공 알림 — 간트 재조회(배지 갱신)용(선택) */
  onCustReqChanged?: () => void
  /** SIF-1 4-3 현장별 방식 변경 성공 알림 — 간트·상세창 재조회(배정/빗금/카드 자동 반영)용(선택) */
  onOrderModeChanged?: () => void
  openMaterialPopup: (taskId: number | null) => void
  handleMaterialChanged: (c: MaterialOrderChange) => void
  deps: Dependency[]
  panelPredIds: any
  setPanelPredIds: any
  panelMemo: any
  setPanelMemo: any
  panelNotice: any
  panelError: any
  panelSaving: any
  /** 상세창 실시간 저장 — over 로 방금 바꾼 값을 넘긴다. partner=true 면 시공파트너 지정·해제도 함께(useGanttPanel#commitPanel) */
  commitPanel: (over?: { memo?: string; name?: string; predIds?: number[]; partnerId?: number | null }, opts?: { partner?: boolean }) => void
  handleDeleteTask: any
  closePanelGuarded: any
  /** 공휴일 조회 상태 — 소요일(작업일 수)·기간 수정 팝오버 계산에 쓴다(A-08) */
  holidayState: HolidayState
  /** 이동 가능 범위(표시 기간 앞뒤 3일) 거울 — 팝오버를 열 때 읽는다(A-11) */
  movableRangeRef: React.MutableRefObject<{ start: Date; end: Date }>
  /** 계획을 간트의 미저장 변경에 반영(A-16). 반영했으면 true */
  applyCascadePlan: (plan: CascadePlan, opts?: { syncOrderDates?: boolean }) => boolean
}

export function GanttDetailPanelHost({
  tasks, selectedPanelTaskId, panelTaskRef, panelWorkTypeName, setPanelWorkTypeName, partners, panelPartnerId,
  panelForeman, setPanelPartnerId, setPanelForeman, handlePartnerSent, panelChangeReason,
  setPanelChangeReason, handlePartnerChanged, panelForemanSignal, selectedSite, cardSup, cardPt, cardCr, onCustReqChanged, onOrderModeChanged, openMaterialPopup,
  handleMaterialChanged, deps, panelPredIds, setPanelPredIds, panelMemo, setPanelMemo, panelNotice,
  panelError, panelSaving, commitPanel, handleDeleteTask, closePanelGuarded, holidayState, movableRangeRef,
  applyCascadePlan,
}: GanttDetailPanelHostProps) {
  // 기간 수정 팝오버 열림·[적용] 직후 칩 강조(1.6초). 다른 공종을 고르거나 상세창이 닫히면 팝오버도 닫힌다.
  const [periodOpen, setPeriodOpen] = useState(false)
  const [periodFlash, setPeriodFlash] = useState(false)
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => { setPeriodOpen(false) }, [selectedPanelTaskId])
  // 시공 진행 요약(④작업요청·⑤작업보고)과 그 세부 내역 팝업(B-08·B-09) — 상세창에서 공종을 바꾸면 팝업은 닫힌다
  const progress = useTaskProgressSummary(selectedPanelTaskId)
  // 다른 에이전트 화면 이동(호스트 주입) — 없으면 [작업현황 등록 ›] 링크를 그리지 않는다
  const host = useSchedulerHost()
  // 결과만 직접등록(SIF-3) 공종인지 — 공종 저장값(배정 시점) 또는 배정된 반장의 등록값
  const panelTask = panelTaskRef.current
  const isDirectPanelTask = panelTask != null && (panelTask.commYn === 'N'
    || isDirectRecord(partners.find(p => p.partnerId === panelTask.assigneePartnerId)?.commYn))
  // SIF-1 '자재파트너가 시공' — 업체명은 자재파트너 카드와 같은 소스(발주 거래처 행)에서 읽는다(캐시 공유).
  // 상세창이 열렸을 때만 조회(자재파트너 카드와 같은 수명) — 간트만 열려 있을 때는 당기지 않는다.
  const matData = useMaterialOrderData(selectedPanelTaskId !== null ? (selectedSite?.scheduleId ?? null) : null)
  const [detailModal, setDetailModal] = useState<typeof WORK_ORDER_DETAIL_KEY | typeof WORK_REPORT_DETAIL_KEY | null>(null)
  useEffect(() => { setDetailModal(null) }, [selectedPanelTaskId])
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current) }, [])
  const applyPeriod = (plan: CascadePlan, opts: { syncOrderDates: boolean }) => {
    const ok = applyCascadePlan(plan, opts)
    if (ok) {
      setPeriodFlash(true)
      if (flashTimer.current) clearTimeout(flashTimer.current)
      flashTimer.current = setTimeout(() => setPeriodFlash(false), 1600)
    }
    return ok
  }
  return (
    <>
      {(() => {
        const liveTask = tasks.find(t => t.taskId === selectedPanelTaskId) ?? null
        if (liveTask) panelTaskRef.current = liveTask
        // 소요일(작업일 수) — 기간 칩과 ⑤작업보고 '보고 n/N일'의 N 이 같은 값
        const panelWorkdays = panelTaskRef.current ? workdaysOrNull(holidayState, holidayState.dates, panelTaskRef.current.startDate, panelTaskRef.current.endDate) : null
        // SIF-1 자재파트너가 시공: 시공파트너 카드를 한 줄로 축약. 업체명은 그 공종 발주 거래처(활성)의 첫 거래처명.
        const constructByVendor = !!panelTaskRef.current?.constructByVendor
        const constructVendorName = constructByVendor && panelTaskRef.current
          ? (matData.rows.find(r => r.taskId === panelTaskRef.current!.taskId && r.orderStatusCode !== 'REPLACED')?.vendorName ?? null)
          : null
        return (
          <TaskDetailPanel
            open={liveTask !== null}
            task={panelTaskRef.current}
            workTypeName={panelWorkTypeName}
            onWorkTypeNameChange={setPanelWorkTypeName}
            onWorkTypeNameCommit={() => commitPanel()}
            workdays={panelWorkdays}
            periodOpen={periodOpen && liveTask !== null}
            onPeriodToggle={() => setPeriodOpen(o => !o)}
            periodFlash={periodFlash}
            periodPopover={liveTask ? anchorRef => (
              <PeriodEditPopover
                key={`${liveTask.taskId}-${liveTask.startDate.getTime()}-${liveTask.endDate.getTime()}-${holidayState.status}`}
                anchorRef={anchorRef}
                task={liveTask}
                tasks={tasks}
                deps={deps}
                holidayState={holidayState}
                movableRange={movableRangeRef.current}
                onApply={applyPeriod}
                onClose={() => setPeriodOpen(false)}
              />
            ) : undefined}
            partners={partners}
            partnerId={panelPartnerId}
            partnerName={panelForeman}
            onPartnerSelect={sel => { setPanelPartnerId(sel.partnerId); setPanelForeman(sel.name); commitPanel({ partnerId: sel.partnerId }, { partner: true }) }}
            onPartnerSent={handlePartnerSent}
            changeReason={panelChangeReason}
            onChangeReason={setPanelChangeReason}
            onPartnerChanged={handlePartnerChanged}
            foremanSignal={panelForemanSignal(panelTaskRef.current)}
            constructByVendor={constructByVendor}
            constructVendorName={constructVendorName}
            partnerProgressSlot={panelTaskRef.current ? (
              <ConfirmProgressSummary
                key={`${panelTaskRef.current.taskId}-${cardPt && cardPt.taskId === panelTaskRef.current.taskId ? cardPt.nonce : 'closed'}`}
                defaultOpen={cardPt !== null && cardPt.taskId === panelTaskRef.current.taskId}
                steps={appendWorkProgressSteps(
                  // 판정 기준은 카드 배지와 같다 — 결과만 직접등록이면 1·2단계 '-'
                  getConfirmProgressSteps(isDirectPanelTask ? { ...panelTaskRef.current, commYn: 'N' } : panelTaskRef.current, {
                    hasPartner: panelTaskRef.current.assigneePartnerId != null,
                    startDate: panelTaskRef.current.startDate,
                    endDate: panelTaskRef.current.endDate,
                  }),
                  // 결과만 직접등록 공종은 마지막 '작업보고' 단계에 [작업현황 등록 ›] 을 붙인다(SIF-3 C-1 처리 기준).
                  // 판정은 시공파트너 카드 배지·안내와 같은 기준 — 공종 저장값이 N 이거나, 배정된 반장의 등록값이 N
                  // 작업요청 단계(첫 단계)는 결과만 직접등록이면 보내지 않는 단계라 조회 결과와 상관없이 '-'(오류 문구·세부 내역 없음)
                  getWorkProgressSteps(progress, panelWorkdays).map((s, i, all) => {
                    if (!isDirectPanelTask) return s
                    if (i === 0) return { title: s.title, text: WORK_PROGRESS_TEXT.none, state: 'pending' as const }
                    // 마지막 단계 — 반장 작업보고가 아니라 영업사원이 등록하는 작업현황이므로 이름도 '작업현황'
                    if (i === all.length - 1) return { ...s, title: WORK_STATUS_STEP_TITLE, action: WORK_STATUS_WRITE_ACTION }
                    return s
                  }),
                )}
                onOpenDetail={key => {
                  if (key === WORK_ORDER_DETAIL_KEY || key === WORK_REPORT_DETAIL_KEY) setDetailModal(key)
                }}
                onAction={host?.openPath ? key => {
                  if (key !== WORK_STATUS_WRITE_ACTION.key) return
                  // 작업현황 작성 화면이 현장 · 공정을 미리 고르도록 스케줄 · 공정을 넘긴다(?scheduleId=&taskId=)
                  host.openPath?.(workStatusWritePath(selectedSite?.scheduleId, panelTaskRef.current?.taskId))
                } : undefined}
              />
            ) : undefined}
            orderModeCard={panelTaskRef.current ? (
              <OrderModeCard
                taskId={panelTaskRef.current.taskId}
                endDate={isoDay(panelTaskRef.current.endDate)}
                value={panelTaskRef.current.orderMode ?? (constructByVendor ? 'VENDOR_CONSTRUCT' : 'SEPARATE')}
                // 확정 반장이 있으면 '자재파트너가 시공' 전환 전에 배정 취소 확인창(SIF-1 1-4)
                confirmedForemanName={
                  panelTaskRef.current.partnerStatus === PARTNER_STATUS.CONFIRMED && panelTaskRef.current.assigneePartnerId != null
                    ? (panelTaskRef.current.foremanName || '시공파트너')
                    : null
                }
                // 방식이 바뀌면 자재파트너 카드(직접발주 여부)도 새 값으로 다시 그리도록 발주 데이터를 함께 다시 받는다
                onChanged={() => { matData.reload(); onOrderModeChanged?.() }}
              />
            ) : undefined}
            materialPartnerCard={panelTaskRef.current && selectedSite?.scheduleId != null ? (
              <MaterialPartnerCard
                task={{
                  taskId: panelTaskRef.current.taskId,
                  workTypeCode: panelTaskRef.current.workTypeCode,
                  startDate: isoDay(panelTaskRef.current.startDate),
                  endDate: isoDay(panelTaskRef.current.endDate),
                }}
                siteId={selectedSite.siteId}
                scheduleId={selectedSite.scheduleId}
                expandNonce={cardSup && cardSup.taskId === panelTaskRef.current.taskId ? cardSup.nonce : undefined}
                onOpenOrderPopup={openMaterialPopup}
                onChanged={handleMaterialChanged}
              />
            ) : undefined}
            customerRequestCard={panelTaskRef.current && selectedSite?.scheduleId != null ? (
              <CustReqCard
                taskId={panelTaskRef.current.taskId}
                siteId={selectedSite.siteId}
                scheduleId={selectedSite.scheduleId}
                expandNonce={cardCr && cardCr.taskId === panelTaskRef.current.taskId ? cardCr.nonce : undefined}
                foremanName={panelTaskRef.current.foremanName || undefined}
                onChanged={onCustReqChanged}
              />
            ) : undefined}
            predecessorCandidates={selectedPanelTaskId !== null ? getPredecessorCandidates(selectedPanelTaskId, tasks, deps) : []}
            predecessorIds={panelPredIds}
            onPredecessorChange={ids => { setPanelPredIds(ids); commitPanel({ predIds: ids }) }}
            memo={panelMemo}
            onMemoChange={setPanelMemo}
            onMemoCommit={() => commitPanel()}
            notice={panelNotice}
            error={panelError}
            saving={panelSaving}
            onDelete={handleDeleteTask}
            onClose={closePanelGuarded}
          />
        )
      })()}
      {selectedPanelTaskId !== null && panelTaskRef.current && detailModal === WORK_ORDER_DETAIL_KEY && (
        <WorkOrderDetailModal
          open
          onClose={() => setDetailModal(null)}
          taskId={selectedPanelTaskId}
          workTypeName={panelTaskRef.current.workTypeName}
          partnerName={panelTaskRef.current.foremanName || undefined}
        />
      )}
      {selectedPanelTaskId !== null && panelTaskRef.current && detailModal === WORK_REPORT_DETAIL_KEY && (
        <WorkReportDetailModal
          open
          onClose={() => setDetailModal(null)}
          taskId={selectedPanelTaskId}
          workTypeName={panelTaskRef.current.workTypeName}
          partnerName={panelTaskRef.current.foremanName || undefined}
          summaryDays={progress.status === 'ready' ? progress.summary.reportDays : undefined}
          workdays={panelTaskRef.current ? workdaysOrNull(holidayState, holidayState.dates, panelTaskRef.current.startDate, panelTaskRef.current.endDate) : null}
        />
      )}
    </>
  )
}
